(ns arthur.domain.png
  "An indexed raster -> one PNG, at integer zoom.

  THE EXPORT'S MASTER FORMAT, and the reasons are all about not resampling.
  Everything above `domain/raster` exists to put hard-edged flat fills into a
  byte buffer; a lossy encoder would put chroma fringes on exactly the edges the
  whole idiom is made of, and a fractional scale would put grey on them. So the
  picture leaves the tool as PNG, and it leaves it at an INTEGER zoom — a pixel
  becomes a block of identical pixels and nothing is interpolated.

  TRUECOLOUR, NOT PALETTED, and that is a deliberate loss. Colour type 3 would be
  the faithful shape — the buffer IS palette indices and a PLTE chunk is the ramp
  — and it would be a third of the bytes into deflate. But the entire purpose of
  this file is to be imported by a program we cannot test against here, and
  type 2 is the type every reader on earth handles. Faithfulness that depends on
  someone else's PNG decoder being complete is not faithfulness. The pixels are
  identical either way; only the file is bigger, and deflate takes most of that
  back because the art is flat.

  `CompressionStream` does the deflating, which is why `encoder` hands back a
  promise. It is the zlib-wrapped variety — RFC 1950, which is what an IDAT
  requires — and getting that wrong is a one-word difference from `deflate-raw`
  and a file no reader will open.

  No DOM. A canvas `toBlob` would be shorter and would put this namespace out of
  reach of node, where the rest of the rasteriser is asserted about; it would also
  hand the encoding decisions to the browser, and the point of this file is that
  they are decisions."
  ;; A `chunk` is the format's own word for its one structural unit, and chunked
  ;; seqs never come up in here, so core's loses the name rather than ours.
  (:refer-clojure :exclude [chunk])
  (:require [arthur.domain.crc32 :as crc32]))

(def ^:private signature
  (js/Uint8Array. #js [0x89 0x50 0x4e 0x47 0x0d 0x0a 0x1a 0x0a]))

(defn- u32! [^js bytes at n]
  (aset bytes at (bit-and (unsigned-bit-shift-right n 24) 0xff))
  (aset bytes (+ at 1) (bit-and (unsigned-bit-shift-right n 16) 0xff))
  (aset bytes (+ at 2) (bit-and (unsigned-bit-shift-right n 8) 0xff))
  (aset bytes (+ at 3) (bit-and n 0xff)))

(defn chunk
  "One PNG chunk: length, type, payload, CRC over type and payload.

  Big-endian throughout, which is the format's and not the machine's — the same
  reason `domain/raster/->rgba` has to ask which way round the machine is and this
  does not."
  [tag ^js payload]
  (let [n   (.-length payload)
        out (js/Uint8Array. (+ n 12))]
    (u32! out 0 n)
    (dotimes [i 4] (aset out (+ 4 i) (.charCodeAt tag i)))
    (.set out payload 8)
    (u32! out (+ 8 n) (crc32/of out 4 (+ 8 n)))
    out))

(defn- ihdr [w h]
  (let [p (js/Uint8Array. 13)]
    (u32! p 0 w)
    (u32! p 4 h)
    (aset p 8 8)                        ; bit depth
    (aset p 9 2)                        ; colour type 2: truecolour RGB
    (aset p 10 0)                       ; deflate, the only compression PNG has
    (aset p 11 0)                       ; adaptive filtering, the only method
    (aset p 12 0)                       ; no interlace
    p))

(defn- deflate!
  "Promise of the zlib stream of `bytes`.

  `CompressionStream` rather than a deflate implementation: it is in every browser
  this tool runs in and in node, so the one thing here that would be hundreds of
  lines is none of them."
  [^js bytes]
  ;; `.stream` first: `pipeThrough` is a ReadableStream's method, not a Blob's.
  (-> (js/Response. (.pipeThrough (.stream (js/Blob. #js [bytes]))
                                  (js/CompressionStream. "deflate")))
      (.arrayBuffer)
      (.then #(js/Uint8Array. %))))

(defn- concat!
  [parts]
  (let [out (js/Uint8Array. (transduce (map #(.-length ^js %)) + 0 parts))]
    (reduce (fn [at ^js part] (.set out part at) (+ at (.-length part))) 0 parts)
    out))

(defn encoder
  "(fn [raster ramp] -> promise of PNG bytes), for one stage size and one zoom.

  Built once per export rather than per frame, in the shape `symbol/resolver`
  already uses: everything that does not change frame to frame is held here. What
  that buys is the scanline scratch, which at zoom 6 is seven megabytes — a
  per-frame allocation of that size is the one thing that would make a long export
  thrash, and it is the same buffer every frame because the stage is.

  FILTER TYPE 2 (Up) ON EVERY ROW, including the first, where PNG defines the
  prior row as zeros and Up therefore degenerates to None. It is chosen for the
  zoom: at zoom 4 three of every four output rows are byte-identical to the one
  above, so Up turns them into runs of zeros and deflate takes them to almost
  nothing. Paletted or not, that is where the size of an upscaled flat-fill frame
  goes."
  [w h zoom]
  (let [zoom   (max 1 (js/Math.floor zoom))
        out-w  (* w zoom)
        out-h  (* h zoom)
        stride (* out-w 3)
        ;; One filter byte per output row, then the filtered row.
        raw    (js/Uint8Array. (* out-h (inc stride)))
        ;; The row as it actually is, kept because Up filters against the
        ;; UNFILTERED row above, not against the stored bytes.
        cur    (js/Uint8Array. stride)
        prev   (js/Uint8Array. stride)
        head   (concat! [signature (chunk "IHDR" (ihdr out-w out-h))])
        tail   (chunk "IEND" (js/Uint8Array. 0))]
    (fn [{:keys [buf] :as _raster} ramp]
      ;; The ramp is read as a flat byte table for the same reason ->rgba reads
      ;; one: `nth` into a vector of vectors is four protocol dispatches a pixel,
      ;; and this walks every pixel of every frame.
      (let [p8 (js/Uint8Array. (* 256 3))]
        (dotimes [i 256]
          (let [c (or (nth ramp i nil) [255 0 255])]
            (aset p8 (* i 3) (nth c 0))
            (aset p8 (+ 1 (* i 3)) (nth c 1))
            (aset p8 (+ 2 (* i 3)) (nth c 2))))
        (.fill prev 0)
        (dotimes [y h]
          (let [srow (* y w)]
            ;; Expand one SOURCE row through the ramp once, repeating each pixel
            ;; `zoom` times across.
            (dotimes [x w]
              (let [p (* 3 (aget buf (+ srow x)))
                    r (aget p8 p) g (aget p8 (+ p 1)) b (aget p8 (+ p 2))]
                (dotimes [k zoom]
                  (let [o (* 3 (+ (* x zoom) k))]
                    (aset cur o r)
                    (aset cur (+ o 1) g)
                    (aset cur (+ o 2) b)))))
            ;; …and emit it `zoom` times down. The second and later copies filter
            ;; to all zeros, which is the whole point of Up here.
            (dotimes [k zoom]
              (let [at (* (+ (* y zoom) k) (inc stride))]
                (aset raw at 2)
                (dotimes [i stride]
                  (aset raw (+ at 1 i)
                        (bit-and (- (aget cur i) (aget prev i)) 0xff)))
                (.set prev cur)))))
        (-> (deflate! raw)
            (.then (fn [z] (concat! [head (chunk "IDAT" z) tail]))))))))
